import React, { useContext, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Form, Formik } from 'formik';

import { subscribe } from 'api/newsletter';
import NewsletterFields from './NewsletterFields/NewsletterFields';
import ButtonCustom from 'components/Common/ButtonCustom/ButtonCustom';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import Trans from 'components/Common/Translation/Trans';
import TurnstileFormikRow, {
  TurnstileRef,
} from 'components/Common/Cloudflare/TurnstileFormikRow';
import Loading from 'components/Common/Loading/Loading';
import useCurrentCountry from 'hooks/useCurrentCountry';
import useNewsletterSubscribe, {
  IniatialValuesType,
} from 'hooks/useNewsletterSubscribe';
import { userContext } from 'contexts/userContext';
import validationSchema from 'schemas/NewsletterSubscribeValidationSchema';
import { getRoutesUri } from 'services/generic';
import { replacePhoneNumberWithoutSpace } from 'services/formatter';
import { gtmAfiEventLab } from 'services/gtm';
import { GTM_FORM_NEWSLETTER_SUBSCRIBE } from 'settings/gtm/gtmForm';
import { GTM_PAGE_OTHER } from 'settings/gtm/gtmPageView';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';

import styles from '../NewsletterPage.module.scss';

type Props = {
  setSubmitStatus: (submitStatus: boolean) => void;
};

export default function NewsletterSubscribe({
  setSubmitStatus,
}: Props): JSX.Element {
  const { t } = useTranslation(['newsletterPage', 'translation']);
  const { isUk } = useCurrentCountry();
  const turnstile = useRef<TurnstileRef>();
  const isTurnstileDesktop = useMediaQuery({ minWidth: '371px' });
  const [isLoading, setLoading] = useState(false);
  const { initialValues } = useNewsletterSubscribe();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { gtmUserCookie } = useContext(userContext);
  const isMobileAndPortraitTablet = useMediaQuery({
    query: MOBILE_AND_TABLET_PORTRAIT,
  });

  async function onSubmit(values: IniatialValuesType) {
    const { mailOption, smsOption, partOption } = values;
    setLoading(true);

    const response = await subscribe({
      ...values,
      cellPhone: replacePhoneNumberWithoutSpace(values.cellPhone || ''),
      mailOption: mailOption === '1',
      smsOption: smsOption === '1',
      partOption: partOption === '1',
    });

    if (response.status === 409) {
      // TODO: Set flash message
      navigate(getRoutesUri<string>('customerLogin'));

      return;
    }

    try {
      if (response.ok) {
        setSubmitStatus(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        gtmAfiEventLab(
          pathname,
          'afiEvent',
          'Newsletter',
          'Inscription',
          gtmUserCookie
        );

        return;
      }

      // Set errors
    } finally {
      turnstile.current?.reset();
      setLoading(false);
    }
  }

  return (
    <div className={styles.subscribe}>
      <div className={styles.header}>
        <h2>
          {isMobileAndPortraitTablet
            ? `${t('LABEL_I_SUBSCRIBE')} -`
            : t('LABEL_I_SUBSCRIBE')}
        </h2>
        <span>
          {t('LABEL_SUBTITLE_1')}
          {isUk && (
            <>
              <br />
              <Trans
                i18nKey={'LABEL_SUBTITLE_2'}
                namespace={'newsletterPage'}
              />
            </>
          )}
        </span>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema(isUk, t)}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        <Form>
          <FormikErrorsGtmLogger
            page_type={GTM_PAGE_OTHER}
            form_type={GTM_FORM_NEWSLETTER_SUBSCRIBE}
          />
          <NewsletterFields />
          <TurnstileFormikRow
            classes={{
              row: styles.captchaRow,
              errorMessage: styles.captchaErrorMessage,
            }}
            turnstileRef={turnstile}
            turnstileProps={{
              className: styles.captchaField,
              options: {
                action: 'newsletter_create',
                size: isTurnstileDesktop ? 'normal' : 'compact',
              },
            }}
          />
          <div className={styles.actionToolbar}>
            <ButtonCustom
              label={t('LABEL_CONFIRM', { ns: 'translation' })}
              color="red"
            />
          </div>
          <RequiredFields />
          <ScrollToFieldError />
        </Form>
      </Formik>
      {isLoading && <Loading loading />}
    </div>
  );
}
